import { TAU } from '../core/math';
import { rr } from '../core/rng';
import { P } from '../world/player';
import { missiles } from '../world/pools';
import { scene } from '../render/scene';
import { SFX } from '../platform/audio';
import { mechEvent } from '../entities/mech-anim';
import { nearestEnemy, shake, hitstop } from './fx';
import { CAM, camLow, camPunch } from './camera';
import { VC, fxExplode, fxLaunch, fxMuzzle, fxShockRing } from '../render/weapon-fx';
/* ============================================================
   TITAN-3 的三把武裝：榴彈砲（普攻）／火箭砲（小招）／分裂彈（大招）
   ============================================================

   三把共用 `missiles` 池（`world/pools.ts`）。差別只在**有沒有目標**：

     `target` 有值  → `sim/projectiles.ts` 每幀把速度 lerp 向目標（追蹤）
     `target` 是 null → 只吃重力 `v.y -= 22·dt`（純拋物線）

   所以「榴彈」不需要新的投射物型別，只要**不給目標、並把初速解成
   會落在指定點的拋物線**（`lob()`）。這也是這台機體與 CHASSIS-0 的
   分野：它的普攻打的是**地面上的一塊**，不是一條直線 ——
   對移動中的目標會落空，那是重砲該有的取捨（GDD §6.2）。

   ⚠️ 這個檔獨立於 `sim/weapons.ts` 只是為了行數（那個檔已經 479 行），
   `WEAPONS` 註冊表仍然只有一份，在 `sim/weapons.ts` 尾端。 */

/** 重力。**必須與 `sim/projectiles.ts` 的 `o.v.y -= 22 * dt` 同值** ——
    這裡解的彈道就是那條積分的解，兩邊不一致的話砲彈永遠打不到落點。 */
var GRAV = 22;
/** 落地判定的高度（`sim/projectiles.ts`：`position.y < 0.3` 就引爆）。 */
var HIT_Y = 0.3;

/** 把 `m` 擺到 (x0,y0,z0)，並解出「t 秒後落在 (tx,tz) 的地面」的初速。

    垂直分量是 `y(t) = y0 + vy·t − ½g·t²  = HIT_Y` 的解。
    水平是等速（拋物線期間沒有推力），所以直接 `距離 / t`。 */
function lob(m, x0, y0, z0, tx, tz, t) {
  m.m.position.set(x0, y0, z0);
  m.v.set((tx - x0) / t, (HIT_Y - y0 + 0.5 * GRAV * t * t) / t, (tz - z0) / t);
}

/** 池化物件的共用重設。**每次 `.get()` 都要跑** —— 上一發留下的
    `split` / `arc` / `trail` 會讓下一發莫名其妙分裂或共用拖尾緞帶
    （同型的坑在 `spawnBullet` 的 `bounce` 與 `fireMissiles` 的 `trail` 記過）。 */
function shell(m, dmg, radius, scale, arc, split) {
  m.m.scale.setScalar(scale);
  m.scale = scale; m.dmg = dmg; m.radius = radius;
  m.arc = arc; m.split = split; m.life = 0; m.trail = -1;
  scene.add(m.m);
}

/* ── 榴彈砲（普攻）───────────────────────────────────────────
   一發、慢、拋物線、落點爆炸。`atkSpeed` 在這把武裝上的語意是
   **彈道的水平速度**（用來反推飛行時間），不是子彈速度 ——
   同一條統計軸，讀出來是「砲彈飛得比較快 = 比較不容易被閃掉」。 */
export function fireMortar() {
  var tgt = nearestEnemy(P.pos, P.s.atkRange, P.faceAim, true);
  if (!tgt) return;
  var mod = P.mod.primary;
  /* 主砲掛在 local −x（`entities/strongman.ts` 的 `arm.position.x = -1.02`），
     `side = −1` 才對得上那隻手 —— 理由與 `fireVulcan` 的長註解相同，
     換機體要重新確認符號。 */
  var side = -1;
  var dist = Math.hypot(tgt.pos.x - P.pos.x, tgt.pos.z - P.pos.z);
  var aim = Math.atan2(tgt.pos.x - P.pos.x, tgt.pos.z - P.pos.z);
  var ox = P.pos.x + Math.sin(aim) * 1.1 + Math.cos(aim) * side * 1.30;
  var oy = P.y + 2.6;
  var oz = P.pos.z + Math.cos(aim) * 1.1 - Math.sin(aim) * side * 1.30;
  /* 飛行時間：距離除以彈速，加一段固定的爬升。下限 0.42 秒是
     「近距離也看得到砲彈飛過去」的最短拍，不然貼臉時它是瞬發的。 */
  var t = Math.max(0.42, dist / Math.max(12, P.s.atkSpeed) + 0.34);
  /* 前置量只給 0.7 —— 給滿的話它變成必中的追蹤武器，那是小招的工作。
     打移動目標會落在後面一點，玩家要自己選開火時機。 */
  var px = tgt.pos.x + tgt.vel.x * t * 0.7, pz = tgt.pos.z + tgt.vel.z * t * 0.7;
  var count = P.s.atkCount + (mod === 'salvo' ? 1 : 0);
  var radius = 3.6 * (P.s.area || 1) * (mod === 'heavy' ? 1.4 : 1);
  var dmg = P.s.atkDmg * (mod === 'heavy' ? 1.25 : 1) * (count > 1 ? 0.8 : 1);
  for (var i = 0; i < count; i++) {
    var m = missiles.get(); if (!m) break;
    /* 多發時把落點沿**垂直於瞄準線**的方向岔開，不是隨機灑 ——
       散射榴彈要讀得出「一排炸過去」而不是「亂噴」。 */
    var off = count === 1 ? 0 : (i - (count - 1) / 2) * 3.0;
    lob(m, ox, oy, oz, px + Math.cos(aim) * off + rr(-0.5, 0.5), pz - Math.sin(aim) * off + rr(-0.5, 0.5), t);
    m.target = null; m.who = 'mech-primary';
    shell(m, dmg, radius, 0.85, 1, 0);
  }
  mechEvent(P.rig, 'fire');
  fxMuzzle(ox, oy, oz, aim, 0.5);
  CAM.push = Math.min(0.6, CAM.push + 0.24);   // 後座比機炮重一階
  SFX.shoot(P.pos.x, P.pos.z); shake(0.03);
}

/* ── 火箭砲（小招）───────────────────────────────────────────
   一發、追蹤、大爆炸。CHASSIS-0 的飛彈是「四發分散清雜魚」，
   這一發是**集中**：傷害與半徑都放在同一顆上，打的是硬目標。
   `midCount` 仍然生效（被動加彈數時會變成兩發），只是基準是 1。 */
export function fireRocket() {
  var mod = P.mod.mid;
  var n = Math.max(1, P.s.midCount);
  var aim = P.faceAim;
  var used: any[] = [];
  for (var i = 0; i < n; i++) {
    var m = missiles.get(); if (!m) break;
    var tgt: any = null;
    for (var k = 0; k < 6; k++) {
      var c = nearestEnemy(P.pos, 52, aim, k < 3);
      if (!c) break;
      if (used.indexOf(c) < 0) { tgt = c; break; }
      used.push(c);
    }
    if (!tgt) tgt = nearestEnemy(P.pos, 70);
    if (tgt) used.push(tgt);
    /* 從掛架側（local +x）射出，先往斜上方推一段再轉向 ——
       `sim/projectiles.ts` 前 0.22 秒不追蹤，那一段就是「發射」的拍子。 */
    var a2 = aim + (i - (n - 1) / 2) * 0.22;
    m.m.position.set(P.pos.x + Math.cos(aim) * 1.25, P.y + 3.0, P.pos.z - Math.sin(aim) * 1.25);
    m.v.set(Math.sin(a2) * 10, 8, Math.cos(a2) * 10);
    m.target = tgt; m.who = 'mech-mid';
    shell(m, P.s.midDmg, 6.0 * (P.s.area || 1), 1.55, 0, mod === 'cluster' ? 3 : 0);
  }
  mechEvent(P.rig, 'mid');
  SFX.missile(); shake(0.22);
  fxLaunch(P.pos.x, P.y + 3.0, P.pos.z, aim, VC.fire);
  fxShockRing(P.pos.x, P.pos.z, VC.fire, 1.6, 8, 0.36, 0.5);
}

/* ── 分裂彈（大招）───────────────────────────────────────────
   以自己為圓心丟出**一圈**榴彈，三波由內往外，每顆落地再分裂成兩顆。

   為什麼分三波而不是一次丟 24 顆：一次丟完只有一個瞬間，而且中央那一圈
   會把外圈的畫面吃掉。三波由內往外擴的節奏讓玩家讀得到「壓過去了」，
   也給了走位空間 —— 站在原地的敵人會被三層都掃到，衝進來的只吃外圈。

   ⚠️ 波次靠 `P.ultT` **跨過時間格線**判斷，不另外開計數欄位 ——
   `P` 上的欄位會進決定性快照，能不加就不加（同 `sim/projectiles.ts`
   的排氣煙用的是同一個技法）。 */
var WAVES = [{ t: 0, r: 6.5, n: 7 }, { t: 0.40, r: 11.5, n: 8 }, { t: 0.80, r: 16.5, n: 9 }];

function burstWave(w, idx) {
  var rad = w.r * (P.s.area || 1) * (P.mod.ult === 'expand' ? 1.35 : 1);
  /* 每一波轉半格，三波疊起來的落點才不會連成三條放射線
     （那會在地上讀成一個星形，而不是一圈）。 */
  var base = P.faceAim + idx * (TAU / (w.n * 2));
  for (var k = 0; k < w.n; k++) {
    var m = missiles.get(); if (!m) break;
    var a = base + (k / w.n) * TAU;
    lob(m, P.pos.x, P.y + 2.8, P.pos.z,
      P.pos.x + Math.sin(a) * rad, P.pos.z + Math.cos(a) * rad, 0.52 + idx * 0.06);
    m.target = null; m.who = 'mech-ult';
    /* `split = 2`：落地爆炸後再彈出兩顆小彈（`sim/projectiles.ts` 的分裂分支，
       各 0.45 傷害、會自己找目標）—— 「分裂彈」這個名字的來源。
       三波 24 顆 × 2 = 48，池只有 40，所以取不到就 break（上面每一發都檢查）。 */
    shell(m, P.s.ultDmg, 4.2 * (P.s.area || 1), 0.95, 1, 2);
  }
  fxShockRing(P.pos.x, P.pos.z, VC.fire, 1.2, rad, 0.40);
  shake(0.16 + idx * 0.04); SFX.missile(P.pos.x, P.pos.z);
}

export function fireRingBurst() {
  P.ultT = 0;
  burstWave(WAVES[0], 0);
  mechEvent(P.rig, 'ult');
  /* 發動的那一拍：腳下的爆閃 + 壓低鏡頭 + 短頓幀。
     長度對齊第一波的飛行時間（0.52 秒），錯開會讀成兩件事。 */
  fxExplode(P.pos.x, P.y + 1.0, P.pos.z, VC.fire, 3.4, 0.9);
  hitstop(0.22); camLow(0.9); camPunch(0.7); shake(0.42);
  SFX.boom(P.pos.x, P.pos.z);
}

export function updateRingBurst(dt) {
  if (P.ultT < 0) return;
  var prev = P.ultT;
  P.ultT += dt;
  for (var i = 1; i < WAVES.length; i++) {
    var w = WAVES[i];
    if (prev < w.t && P.ultT >= w.t) burstWave(w, i);
  }
  /* 最後一波之後還要留一段給砲彈飛完，否則冷卻圖示會比畫面早結束。
     `ultDur` 就是那段長度（機體資料裡設 1.4）。 */
  if (P.ultT >= Math.max(P.s.ultDur, WAVES[WAVES.length - 1].t + 0.2)) P.ultT = -1;
}
